import { useFetcher } from "react-router";
import Star from "~/components/atoms/Star";
import { RouteIds, useRouteData } from "~/lib/routing";
import { useCallback } from "react";
import type { Route } from "~types/apis/+types/favoriteCollege";
import { CollegeSignUpModalContent } from "~/components/organisms/CollegeSignUpModalContent";
import { useModal } from "~/components/providers/ModalProvider";
import { MOCK_ATHLETE_ID } from "~/lib/utils";
import { useAuthenticityToken } from "remix-utils/csrf/react";

interface CollegeFavoriteStarProps {
    favorited: boolean;
    collegeId: string;
    collegeName: string;
}

export default function CollegeFavoriteStar({
    collegeId,
    collegeName,
    favorited,
}: CollegeFavoriteStarProps) {
    const { authenticated, FEATURES } = useRouteData(RouteIds.ROOT);
    const fetcher = useFetcher<Route.ComponentProps["actionData"]>();
    const { openModal, closeModal } = useModal();
    const csrf = useAuthenticityToken();
    const submitFavorite = useCallback(
        (intent: "add" | "delete") => {
            void fetcher.submit(
                {
                    athleteId: MOCK_ATHLETE_ID,
                    collegeId,
                    intent,
                    csrf,
                },
                { method: "post", action: "/college-recruiting/favorite/college" },
            );
        },
        [collegeId, fetcher, csrf],
    );

    if (!FEATURES.BETAExperience) {
        return <div className="flex size-[36px]" />;
    }

    const isFavorited = fetcher.formData ? fetcher.formData.get("intent") === "add" : favorited;
    const onClick: React.MouseEventHandler = e => {
        e.preventDefault();
        e.stopPropagation();

        if (!authenticated) {
            openModal({
                type: "alert",
                body: <CollegeSignUpModalContent collegeId={collegeId} collegeName={collegeName} />,
                onClose: closeModal,
            });
            return;
        }

        submitFavorite(isFavorited ? "delete" : "add");
    };

    return (
        <>
            <button
                className="flex size-[36px] items-center justify-center rounded-full bg-white hover:bg-blue-20"
                onClick={onClick}
                data-testid={`favoriteStar-${collegeId}`}
            >
                <Star outline="blue" fill={isFavorited ? "blue" : undefined} size={"lg"} />
            </button>
        </>
    );
}
